import { useEffect, useMemo, useRef, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  Copy,
  CreditCard,
  Folder,
  Heart,
  Home,
  LogOut,
  Settings,
  ShieldCheck,
  Sparkles,
  Upload,
  Wallet,
  Wrench,
  BarChart3,
  User as UserIcon,
  Users,
  Mail,
  Monitor,
  Pencil,
} from 'lucide-react';
import { formatTierName } from '../lib/formatters';

interface AccountMenuProps {
  user: User;
  tier?: string | null;
  genCredits: number | null;
  cashCredits: number | null;
  isAdmin: boolean;
  onLogout: () => void;
}

function formatCredits(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '0';
  if (value >= 1000000) return `${Math.floor(value / 1000) / 100}M`;
  if (value >= 1000) return `${Math.floor(value / 10) / 100}k`;
  return `${Math.floor(value)}`;
}

function tierClass(tier?: string | null): string {
  if (tier === 'studio') return 'text-purple-700 bg-purple-50 border-purple-200';
  if (tier === 'creator') return 'text-orange-700 bg-orange-50 border-orange-200';
  return 'text-slate-600 bg-slate-50 border-slate-200';
}

export default function AccountMenu({ user, tier, genCredits, cashCredits, isAdmin, onLogout }: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const email = user.email || 'Signed in';
  const initials = useMemo(() => {
    const base = (email.split('@')[0] || 'U').trim();
    const parts = base.split(/[._-]/).filter(Boolean);
    const a = parts[0]?.[0] ?? base[0] ?? 'U';
    const b = parts[1]?.[0] ?? '';
    return (a + b).toUpperCase();
  }, [email]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const el = menuRef.current;
      if (!el) return;
      if (e.target instanceof Node && !el.contains(e.target)) {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('touchstart', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  const copyUserId = async () => {
    try {
      await navigator.clipboard.writeText(user.id);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      setCopied(false);
    }
  };

  const close = () => setOpen(false);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 hover:bg-slate-50 transition-colors"
      >
        <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center text-white text-xs font-bold">
          {initials}
        </div>
        <div className="hidden sm:flex flex-col items-start leading-tight max-w-[180px]">
          <span className="text-xs font-semibold text-slate-800 truncate">{email}</span>
          <span className={`text-[10px] font-semibold inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${tierClass(tier)}`}>
            {formatTierName(tier)}
            {isAdmin ? <ShieldCheck className="h-3 w-3" /> : null}
          </span>
        </div>
        <ChevronDown className="h-4 w-4 text-slate-500" />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account menu"
          className="absolute right-0 mt-2 w-[320px] rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden z-50"
        >
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <Mail className="h-4 w-4 text-slate-500" />
                  <span className="truncate">{email}</span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-[11px] font-semibold text-orange-700">
                    <Sparkles className="h-3 w-3" />
                    {formatCredits(genCredits)} tokens
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                    <Wallet className="h-3 w-3" />
                    ${formatCredits(cashCredits)}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={copyUserId}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <Copy className="h-3.5 w-3.5" />
                {copied ? 'Copied' : 'User ID'}
              </button>
            </div>
          </div>

          <div className="p-2 text-sm">
            <Link to="/dashboard" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors">
              <Home className="h-4 w-4 text-slate-600" />
              <span className="font-medium text-slate-800">Dashboard</span>
            </Link>
            <Link to="/assets" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors">
              <Folder className="h-4 w-4 text-slate-600" />
              <span className="font-medium text-slate-800">Library</span>
            </Link>
            <Link to="/jobs" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors">
              <Sparkles className="h-4 w-4 text-slate-600" />
              <span className="font-medium text-slate-800">Jobs</span>
            </Link>
            <Link to="/redeem" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors">
              <Wrench className="h-4 w-4 text-slate-600" />
              <span className="font-medium text-slate-800">Redeem & Salvage</span>
            </Link>
            <Link to="/marketplace/liked" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors">
              <Heart className="h-4 w-4 text-slate-600" />
              <span className="font-medium text-slate-800">Saved listings</span>
            </Link>

            <div className="my-2 h-px bg-slate-200" />

            <Link to="/credits" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors">
              <Sparkles className="h-4 w-4 text-slate-600" />
              <span className="font-medium text-slate-800">Buy tokens</span>
            </Link>
            <Link to="/billing" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors">
              <CreditCard className="h-4 w-4 text-slate-600" />
              <span className="font-medium text-slate-800">Billing</span>
            </Link>
            <Link to="/wallet" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors">
              <Wallet className="h-4 w-4 text-slate-600" />
              <span className="font-medium text-slate-800">Wallet</span>
            </Link>
            <Link to="/upload" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors">
              <Upload className="h-4 w-4 text-slate-600" />
              <span className="font-medium text-slate-800">Upload asset</span>
            </Link>

            <div className="my-2 h-px bg-slate-200" />

            <Link to="/profile" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors">
              <UserIcon className="h-4 w-4 text-slate-600" />
              <span className="font-medium text-slate-800">My creator profile</span>
            </Link>
            <Link to="/profile/edit" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors">
              <Pencil className="h-4 w-4 text-slate-600" />
              <span className="font-medium text-slate-800">Edit creator profile</span>
            </Link>
            <Link to="/account/profile" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors">
              <Settings className="h-4 w-4 text-slate-600" />
              <span className="font-medium text-slate-800">Settings</span>
            </Link>
            <Link to="/creators" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors">
              <Users className="h-4 w-4 text-slate-600" />
              <span className="font-medium text-slate-800">Creators</span>
            </Link>
            <Link to="/viewer" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors">
              <Monitor className="h-4 w-4 text-slate-600" />
              <span className="font-medium text-slate-800">Viewer</span>
            </Link>
            <Link to="/analytics" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors">
              <BarChart3 className="h-4 w-4 text-slate-600" />
              <span className="font-medium text-slate-800">Analytics</span>
            </Link>

            {isAdmin && (
              <>
                <div className="my-2 h-px bg-slate-200" />
                <div className="px-3 py-1 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Admin</div>
                <Link to="/admin" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-purple-50 transition-colors">
                  <ShieldCheck className="h-4 w-4 text-purple-700" />
                  <span className="font-semibold text-purple-800">Admin dashboard</span>
                </Link>
                <Link to="/admin/tiers" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-purple-50 transition-colors">
                  <Wrench className="h-4 w-4 text-purple-700" />
                  <span className="font-semibold text-purple-800">Tier settings</span>
                </Link>
                <Link to="/debug-user" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-purple-50 transition-colors">
                  <Wrench className="h-4 w-4 text-purple-700" />
                  <span className="font-semibold text-purple-800">Debug user</span>
                </Link>
                <Link to="/supabase-test" onClick={close} role="menuitem" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-purple-50 transition-colors">
                  <Wrench className="h-4 w-4 text-purple-700" />
                  <span className="font-semibold text-purple-800">Supabase tests</span>
                </Link>
              </>
            )}
          </div>

          <div className="border-t border-slate-200 p-2 bg-white">
            <button
              type="button"
              onClick={() => {
                close();
                onLogout();
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
